import React, { useState, useEffect, useMemo } from 'react';

// TopImage 컴포넌트를 App 외부로 이동
const TopImage = ({ floorKey, topImages, topSlideIndex, floors }) => {
  const [imageExists, setImageExists] = useState(false);
  
  const guideImageName = floors[floorKey]?.guideImageName;
  const imageUrl = guideImageName ? `/images/guide/${guideImageName}` : '';

  useEffect(() => {
    if (floorKey !== 'initial' && imageUrl) {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => setImageExists(true);
      img.onerror = () => setImageExists(false);
    } else {
      setImageExists(false);
    }
  }, [floorKey, imageUrl]);

  if (floorKey === 'initial') {
    if (topImages.length === 0) return <p className="loading-text">상단 이미지를 로드 중입니다...</p>;

    if (topImages.length === 1) {
      return (
        <div className="slider-wrapper">
          <img src={topImages[0]} alt="hidden" className="hidden-spacer" />
          <div className="slider-container">
            <div className="slide active" style={{ opacity: 1, left: 0 }}>
              <img src={topImages[0]} alt="Top Image" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="slider-wrapper">
        {topImages.length > 0 && <img src={topImages[0]} alt="hidden" className="hidden-spacer" />}
        <div className="slider-container">
          {topImages.map((path, i) => (
            <div key={path} className={`slide ${i === topSlideIndex ? 'active' : ''} ${i === (topSlideIndex - 1 + topImages.length) % topImages.length ? 'exit' : ''}`}>
              <img src={path} alt={`Top Slide ${i}`} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (imageExists) {
      return <img src={imageUrl} alt={`${floorKey} 안내도`} className="floor-plan-image" />;
  }
  return <p className="loading-text">해당 층의 안내도 정보가 없습니다.</p>;
};

function App() {
  const [floors, setFloors] = useState({});
  const [selectedFloor, setSelectedFloor] = useState('initial');
  const [topImages, setTopImages] = useState([]);
  const [mainImages, setMainImages] = useState([]);
  const [headImage, setHeadImage] = useState('');
  const [topSlideIndex, setTopSlideIndex] = useState(0);
  const [mainSlideIndex, setMainSlideIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [language, setLanguage] = useState('ko'); // 'ko', 'en', 'cn', 'jp'

  // URL 파라미터에서 zoom 값 읽기
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zoom = parseFloat(params.get('zoom'));
    if (!isNaN(zoom) && zoom > 0) {
      setZoomLevel(zoom);
    }
  }, []);

  // API 호출
  useEffect(() => {
    // Floors data
    fetch(`/api/floors?lang=${language}`)
      .then(res => res.json())
      .then(data => setFloors(data))
      .catch(error => console.error('Error fetching floors:', error));

    // Static images (only fetch once)
    if (topImages.length === 0) {
        fetch('/api/top-images')
            .then(res => res.json())
            .then(data => setTopImages(data))
            .catch(error => console.error('Error fetching top images:', error));
    }
    if (mainImages.length === 0) {
        fetch('/api/main-images')
            .then(res => res.json())
            .then(data => setMainImages(data))
            .catch(error => console.error('Error fetching main images:', error));
    }
    if (!headImage) {
        fetch('/api/head-image')
            .then(res => res.text())
            .then(data => setHeadImage(data))
            .catch(error => console.error('Error fetching head image:', error));
    }
  }, [language, topImages.length, mainImages.length, headImage]);

  // 사용자 활동 감지 (타임아웃 시 새로고침)
  useEffect(() => {
    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => window.location.reload(), 15000);
    };

    const handleActivity = () => {
      resetTimer();
    };

    if (selectedFloor !== 'initial') {
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('wheel', handleActivity);
        resetTimer();
    }

    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('wheel', handleActivity);
    };
  }, [selectedFloor]);

  // 슬라이드 로직
  useEffect(() => {
    if (selectedFloor !== 'initial') return;
    
    const topInterval = topImages.length > 1 ? setInterval(() => {
      setTopSlideIndex(prev => (prev + 1) % topImages.length);
    }, 5000) : null;

    const mainInterval = mainImages.length > 1 ? setInterval(() => {
      setMainSlideIndex(prev => (prev + 1) % mainImages.length);
    }, 5000) : null;

    return () => {
      if (topInterval) clearInterval(topInterval);
      if (mainInterval) clearInterval(mainInterval);
    };
  }, [selectedFloor, topImages, mainImages]);

  const handleFloorClick = (floor) => {
    if (floor !== '18F') setSelectedFloor(floor);
  };

  const handleHeadClick = () => {
    setSelectedFloor('initial');
  };

  // zoomLevel에 따라 스타일 객체 생성 (useMemo로 최적화)
  const containerStyle = useMemo(() => ({
    transform: `scale(${zoomLevel})`,
    transformOrigin: 'top left',
    width: `${100 / zoomLevel}%`,
    height: `${100 / zoomLevel}%`,
  }), [zoomLevel]);

  const sortedFloors = useMemo(() => {
    return Object.keys(floors).sort((a, b) => {
      const aNum = parseInt(a.replace('F', '').replace('B', '-'));
      const bNum = parseInt(b.replace('F', '').replace('B', '-'));
      return bNum - aNum; // 내림차순 정렬 (18F, 17F, ...)
    });
  }, [floors]);

  return (
    <div className="floor-guide-container" style={zoomLevel !== 1 ? containerStyle : {}}>
      {headImage && (
        <div className="head-section" onClick={handleHeadClick}>
          <img src={headImage} alt="Header" />
        </div>
      )}

      <div className="top-section">
        <TopImage 
          floorKey={selectedFloor} 
          topImages={topImages} 
          topSlideIndex={topSlideIndex}
          floors={floors}
        />
      </div>

      <div className="bottom-section">
        <div className="floor-list">
          <ul>
            <li className="language-switcher-container">
              <div className="language-switcher">
                <img 
                  src="/flags/ko.png" 
                  alt="Korean" 
                  className={language === 'ko' ? 'active' : ''}
                  onClick={() => setLanguage('ko')} 
                />
                <img 
                  src="/flags/us.png" 
                  alt="English" 
                  className={language === 'en' ? 'active' : ''}
                  onClick={() => setLanguage('en')} 
                />
                <img 
                  src="/flags/cn.png" 
                  alt="Chinese" 
                  className={language === 'cn' ? 'active' : ''}
                  onClick={() => setLanguage('cn')} 
                />
                <img 
                  src="/flags/jp.png" 
                  alt="Japanese" 
                  className={language === 'jp' ? 'active' : ''}
                  onClick={() => setLanguage('jp')}
                />
              </div>
            </li>
            {sortedFloors.map(floorKey => (
              <li key={floorKey} className={`${selectedFloor === floorKey ? 'active' : ''} ${floorKey === '18F' ? 'disabled' : ''}`} onClick={() => handleFloorClick(floorKey)}>
                <span className="floor-number">{floorKey}</span>
                <span className="floor-name">{floors[floorKey]?.name}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="floor-details">
          {selectedFloor === 'initial' ? (
            mainImages.length === 0 ? (
              <p className="loading-text">메인 이미지를 로드 중입니다...</p>
            ) : mainImages.length === 1 ? (
              <div className="slider-wrapper">
                 <img src={mainImages[0]} alt="hidden" className="hidden-spacer" />
                 <div className="main-slider-container">
                    <div className="slide active" style={{ opacity: 1, left: 0 }}>
                      <img src={mainImages[0]} alt="Main Image" />
                    </div>
                 </div>
              </div>
            ) : (
              <div className="slider-wrapper">
                 {mainImages.length > 0 && <img src={mainImages[0]} alt="hidden" className="hidden-spacer" />}
                <div className="main-slider-container">
                  {mainImages.map((path, i) => (
                    <div key={path} className={`slide ${i === mainSlideIndex ? 'active' : ''} ${i === (mainSlideIndex - 1 + mainImages.length) % mainImages.length ? 'exit' : ''}`}>
                      <img src={path} alt={`Main Slide ${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            )
          ) : (
            floors[selectedFloor] && (
              <>
                <div className="details-header">
                  <h3>{selectedFloor}</h3>
                  <p>{floors[selectedFloor].name}</p>
                </div>
                <div className="brand-grid">
                  {floors[selectedFloor].brands.map(brand => (
                    <div key={brand.name} className="brand-card">
                      <img src={`/images/floor/${brand.imageName}`} alt={brand.name} />
                      <p>{brand.name}</p>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
